// Morrow — Routines (left-nav → Routines).
//
// A Routine is just a Workflow that runs on a clock. Creating one is dead simple: a
// name, a description of what it should do, and WHEN it runs (alarm-clock style). Under
// the hood it creates a workflow (the description is the intent the AI builds from) plus
// a schedule trigger. Routines listed here = workflows that have a schedule trigger.
import {
  AlarmClockIcon,
  ChevronDownIcon,
  CopyIcon,
  Loader2Icon,
  PlusIcon,
  Trash2Icon,
} from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "@tanstack/react-router";

import { apiUrl } from "../../apiBase";
import { cached, invalidateCache } from "../../dataCache";
import { maybeOpenUpgradeFrom402 } from "../../upgradeModal";
import { useI18n } from "~/i18n";
import { useMorrowClerkAuth } from "../clerk/morrowClerkAuth";
import { Button } from "../ui/button";
import { Spinner } from "../ui/spinner";
import { toastManager } from "../ui/toast";
import { MorrowWorkspacePage } from "../workspace/MorrowWorkspacePage";
import { InlineRename } from "./InlineRename";
import { createTriggerFor } from "./WorkflowsPage";
import { cronToHumanLocalized, dowLabels, runDetailLabel } from "./scheduleI18n";
import { browserTimezone, buildCron, nextLocalRuns, type Cadence } from "./scheduleUi";
import { RunStepsPanel, StepStatusIcon } from "./RunStepsPanel";

type Workflow = { id: string; name: string; instruction: string };
type WfTrigger = {
  id: string;
  workflowId: string;
  kind: string;
  scheduleCron: string | null;
};
/** A routine's past runs — the same shape the Workflows list uses, so both pages can offer the
 * same "open this run's conversation" jump instead of a dead count. */
type Run = {
  id: string;
  workflowId: string;
  source: string;
  detail: string | null;
  status: string;
  createdAt: string;
  threadId: string | null;
};

const PRIMARY_BTN =
  "inline-flex items-center justify-center gap-1.5 rounded-md border border-border bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground transition-opacity hover:opacity-90 disabled:opacity-60";
const GHOST =
  "inline-flex items-center justify-center gap-1.5 rounded-md border border-border/60 px-3 py-1.5 text-sm font-medium text-muted-foreground transition hover:bg-muted/50 hover:text-foreground disabled:opacity-60";
const INPUT =
  "w-full rounded-md border border-border bg-transparent px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground/60 focus:outline-none focus:ring-2 focus:ring-primary/40";

async function getJson<T>(path: string): Promise<T | null> {
  try {
    const res = await fetch(apiUrl(path), { headers: { accept: "application/json" } });
    return res.ok ? ((await res.json()) as T) : null;
  } catch {
    return null;
  }
}
async function postJson<T = unknown>(
  path: string,
  body: Record<string, unknown>,
): Promise<T | null> {
  try {
    const res = await fetch(apiUrl(path), {
      method: "POST",
      headers: { "content-type": "application/json", accept: "application/json" },
      body: JSON.stringify(body),
    });
    // A 402 plan gate pops the upgrade modal (dark until enforcement is on).
    if (await maybeOpenUpgradeFrom402(res)) return null;
    return res.ok ? ((await res.json()) as T) : null;
  } catch {
    return null;
  }
}

/** The alarm-clock: pick WHEN it runs (cadence + time), see it in plain English + next runs. */
function SchedulePicker({
  cron,
  tz,
  onChange,
}: {
  cron: string;
  tz: string;
  onChange: (cron: string) => void;
}) {
  const { t } = useI18n();
  const [cadence, setCadence] = useState<Cadence>("daily");
  const [time, setTime] = useState("09:00");
  const [dow, setDow] = useState(1);
  const [minute, setMinute] = useState(0);
  const [custom, setCustom] = useState("0 9 * * *");

  useEffect(() => {
    onChange(buildCron({ cadence, time, dow, minute, custom }));
  }, [cadence, time, dow, minute, custom, onChange]);

  const preview = useMemo(() => nextLocalRuns(cron, 3, new Date()), [cron]);
  const days = useMemo(() => dowLabels(t), [t]);

  return (
    <div className="flex flex-col gap-2.5">
      <div className="flex flex-wrap items-center gap-1.5">
        {(
          [
            ["daily", t("workflows.composer.cadence.daily")],
            ["weekdays", t("workflows.composer.cadence.weekdays")],
            ["weekly", t("workflows.composer.cadence.weekly")],
            ["hourly", t("workflows.composer.cadence.hourly")],
            ["custom", t("workflows.composer.cadence.custom")],
          ] as ReadonlyArray<[Cadence, string]>
        ).map(([val, label]) => (
          <button
            key={val}
            type="button"
            onClick={() => setCadence(val)}
            className={`rounded-md border px-2.5 py-1 text-xs font-medium transition ${
              cadence === val
                ? "border-primary/50 bg-primary/10 text-primary"
                : "border-border/60 text-muted-foreground hover:text-foreground"
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      <div className="flex flex-wrap items-center gap-2">
        {cadence === "weekly" ? (
          <select
            className={`${INPUT} w-auto`}
            value={dow}
            onChange={(e) => setDow(Number(e.target.value))}
          >
            {days.map((d, i) => (
              <option key={d} value={i}>
                {d}
              </option>
            ))}
          </select>
        ) : null}
        {cadence === "hourly" ? (
          <label className="flex items-center gap-1.5 text-xs text-muted-foreground">
            {t("workflows.composer.atMinute")}
            <input
              type="number"
              min={0}
              max={59}
              className={`${INPUT} w-16`}
              value={minute}
              onChange={(e) => setMinute(Math.min(59, Math.max(0, Number(e.target.value) || 0)))}
            />
          </label>
        ) : cadence === "custom" ? (
          <input
            className={`${INPUT} flex-1 font-mono`}
            placeholder={t("workflows.composer.cronPlaceholder")}
            value={custom}
            onChange={(e) => setCustom(e.target.value)}
          />
        ) : (
          <label className="flex items-center gap-1.5 text-xs text-muted-foreground">
            {t("workflows.composer.atTime")}
            <input
              type="time"
              className={`${INPUT} w-auto`}
              value={time}
              onChange={(e) => setTime(e.target.value)}
            />
          </label>
        )}
      </div>

      <div className="rounded-md border border-border/50 bg-muted/20 px-2.5 py-2">
        <div className="flex flex-wrap items-center gap-1.5 text-[13px] font-medium text-foreground">
          <AlarmClockIcon className="size-3.5 text-primary" />
          <span>{cronToHumanLocalized(cron, t)}</span>
          <span className="text-[11px] font-normal text-muted-foreground/70">· {tz}</span>
        </div>
        {preview.length > 0 ? (
          <div className="mt-1 flex flex-wrap gap-1.5">
            {preview.map((r, i) => (
              <span
                key={i}
                className="rounded bg-background px-1.5 py-0.5 text-[10px] text-muted-foreground ring-1 ring-border/50"
              >
                {i === 0 ? t("workflows.composer.schedule.next") : ""}
                {r}
              </span>
            ))}
          </div>
        ) : (
          <div className="mt-1 text-[10px] text-muted-foreground/60">
            {t("workflows.composer.schedule.invalidCron")}
          </div>
        )}
      </div>
    </div>
  );
}

export function RoutinesPage() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const [workflows, setWorkflows] = useState<Workflow[]>([]);
  const [triggers, setTriggers] = useState<WfTrigger[]>([]);
  const [runsByWorkflow, setRunsByWorkflow] = useState<Record<string, Run[]>>({});
  const [loading, setLoading] = useState(true);
  /** Which routine's runs are open, and which run inside it is expanded. */
  const [openRuns, setOpenRuns] = useState<string | null>(null);
  const [selectedRunId, setSelectedRunId] = useState<string | null>(null);

  const [creating, setCreating] = useState(false);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [cron, setCron] = useState("0 9 * * *");
  const [tz] = useState(() => browserTimezone());
  const [busy, setBusy] = useState(false);
  /** Which routine is mid-duplicate, so its icon can spin instead of looking dead. */
  const [duplicating, setDuplicating] = useState<string | null>(null);
  /** A FAILED load is not an empty account — same bug this page shared with Workflows. */
  const [loadFailed, setLoadFailed] = useState(false);
  const { isLoaded, isSignedIn } = useMorrowClerkAuth();

  const refresh = useCallback(async () => {
    // Same cached reads as the Workflows page, so bouncing between the two is free. Writes below
    // invalidate first (see `refreshNow`).
    const [wf, wtr, rn] = await Promise.all([
      // ?brief=1: the routines list shows a name and a schedule, not a graph. See WorkflowsPage.
      cached("/api/morrow/workflows?brief=1", () =>
        getJson<{ workflows?: Workflow[] }>("/api/morrow/workflows?brief=1"),
      ),
      cached("/api/morrow/workflow-triggers", () =>
        getJson<{ triggers?: WfTrigger[] }>("/api/morrow/workflow-triggers"),
      ),
      // NOT cached — see WorkflowsPage: a stale runs list reads as "it did not start".
      getJson<{ runs?: Run[] }>("/api/morrow/workflows/runs"),
    ]);
    if (!wf) {
      // Keep what is on screen; "you have no routines" must come from a load that worked.
      setLoadFailed(true);
      setLoading(false);
      return;
    }
    setLoadFailed(false);
    setWorkflows(wf.workflows ?? []);
    setTriggers((wtr?.triggers ?? []).filter((tr) => tr.kind === "schedule"));
    const byWorkflow: Record<string, Run[]> = {};
    for (const r of rn?.runs ?? []) (byWorkflow[r.workflowId] ??= []).push(r);
    setRunsByWorkflow(byWorkflow);
    setLoading(false);
  }, []);

  useEffect(() => {
    // Wait for the Clerk session: a fetch during bootstrap 401s and used to empty the page.
    if (!isLoaded) return;
    void refresh();
  }, [refresh, isLoaded, isSignedIn]);

  /** Re-read after a write: the cached list is exactly what must not be believed. */
  const refreshNow = useCallback(async () => {
    invalidateCache("/api/morrow/workflow");
    await refresh();
  }, [refresh]);

  /**
   * Rename from the list, through the same name-only route the Workflows page uses — a routine is
   * a workflow with a schedule, so there is nothing routine-specific about its name. Optimistic;
   * the component restores the old name when this returns false.
   */
  const renameRoutine = useCallback(
    async (id: string, name: string): Promise<boolean> => {
      const previous = workflows;
      setWorkflows((current) => current.map((w) => (w.id === id ? { ...w, name } : w)));
      const res = await postJson<{ name?: string }>("/api/morrow/workflows/rename", { id, name });
      if (!res) {
        setWorkflows(previous);
        toastManager.add({ type: "error", title: t("workflows.rename.failed") });
        return false;
      }
      return true;
    },
    [workflows, t],
  );

  /**
   * Copy the whole routine — the workflow AND its schedule, which is what the server's
   * /duplicate does. Deliberately NOT a mirror of the delete button beside it: that one
   * removes only the schedule triggers (leaving the workflow behind), and copying only the
   * schedule would produce a second clock pointing at the same workflow.
   */
  const duplicateRoutine = useCallback(
    async (workflowId: string) => {
      setDuplicating(workflowId);
      await postJson("/api/morrow/workflows/duplicate", { id: workflowId });
      await refreshNow();
      setDuplicating(null);
    },
    [refresh],
  );

  // Routines = workflows that have at least one schedule trigger, with their cron(s).
  const routines = useMemo(() => {
    const byWf = new Map<string, WfTrigger[]>();
    for (const tr of triggers) {
      const list = byWf.get(tr.workflowId) ?? [];
      list.push(tr);
      byWf.set(tr.workflowId, list);
    }
    return workflows
      .filter((w) => byWf.has(w.id))
      .map((w) => ({ workflow: w, schedules: byWf.get(w.id) ?? [] }));
  }, [workflows, triggers]);

  const create = useCallback(async () => {
    if (!name.trim() || !cron.trim()) return;
    setBusy(true);
    const created = await postJson<{ id?: string }>("/api/morrow/workflows", {
      name: name.trim(),
      description: description.trim().slice(0, 200),
      // The description IS the intent — the builder auto-builds the flow from it.
      instruction: description.trim(),
    });
    if (created?.id) {
      await createTriggerFor(created.id, {
        uid: "s",
        kind: "schedule",
        cron: cron.trim(),
        tz,
        summary: cronToHumanLocalized(cron, t),
      });
      setBusy(false);
      setCreating(false);
      setName("");
      setDescription("");
      // Open the builder so the AI fleshes out the flow from the description.
      void navigate({ to: "/workflows/$id", params: { id: created.id }, search: { tab: "edit" } });
    } else {
      setBusy(false);
    }
  }, [name, description, cron, tz, navigate, t]);

  return (
    <MorrowWorkspacePage
      title={t("routines.title")}
      subtitle={t("routines.subtitle")}
      icon={<AlarmClockIcon />}
      wide
      contentClassName="max-w-3xl mx-auto w-full"
      action={
        !creating ? (
          <Button
            type="button"
            size="sm"
            variant="ghost"
            className="text-sky-500 [--control-icon-color:var(--color-sky-500)] hover:bg-sky-500/10 hover:text-sky-500"
            onClick={() => setCreating(true)}
          >
            <PlusIcon aria-hidden="true" />
            {t("routines.new")}
          </Button>
        ) : null
      }
    >
      {creating ? (
        <div className="flex flex-col gap-3 rounded-xl border border-border/70 bg-card p-4 shadow-sm">
          <input
            className={INPUT}
            placeholder={t("routines.namePlaceholder")}
            value={name}
            onChange={(e) => setName(e.target.value)}
            autoFocus
          />
          <textarea
            className={`${INPUT} min-h-[80px] resize-y`}
            placeholder={t("routines.descriptionPlaceholder")}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
          <div>
            <div className="mb-1.5 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
              {t("routines.whenItRuns")}
            </div>
            <SchedulePicker cron={cron} tz={tz} onChange={setCron} />
          </div>
          <div className="flex items-center justify-end gap-2">
            <button
              type="button"
              className={GHOST}
              onClick={() => setCreating(false)}
              disabled={busy}
            >
              {t("routines.cancel")}
            </button>
            <button
              type="button"
              className={PRIMARY_BTN}
              onClick={() => void create()}
              disabled={busy || !name.trim() || !cron.trim()}
            >
              {busy ? <Loader2Icon className="size-4 animate-spin" /> : t("routines.create")}
            </button>
          </div>
        </div>
      ) : null}

      {loading ? (
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Spinner /> {t("routines.loading")}
        </div>
      ) : loadFailed && routines.length === 0 ? (
        <div className="rounded-xl border border-dashed border-border/60 p-8 text-center">
          <AlarmClockIcon className="mx-auto size-8 text-muted-foreground/40" />
          <p className="mt-2 text-sm text-muted-foreground">{t("workflows.loadFailed")}</p>
          <Button
            type="button"
            size="sm"
            variant="outline"
            className="mt-3"
            onClick={() => void refreshNow()}
          >
            {t("workflows.loadRetry")}
          </Button>
        </div>
      ) : routines.length === 0 ? (
        <div className="rounded-xl border border-dashed border-border/60 p-8 text-center">
          <AlarmClockIcon className="mx-auto size-8 text-muted-foreground/40" />
          <p className="mt-2 text-sm text-muted-foreground">{t("routines.empty")}</p>
        </div>
      ) : (
        <ul className="flex flex-col gap-2">
          {routines.map(({ workflow, schedules }) => {
            const nextRun = nextLocalRuns(schedules[0]?.scheduleCron ?? "", 1, new Date())[0];
            const runs = runsByWorkflow[workflow.id] ?? [];
            return (
              <li
                key={workflow.id}
                className="flex flex-col gap-2 rounded-xl border border-border/70 bg-card p-3"
              >
                <div className="flex items-center gap-3">
                  <span className="flex size-9 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
                    <AlarmClockIcon className="size-4.5" />
                  </span>
                  <div className="min-w-0 flex-1">
                    {/* Click the name to rename in place. Outside the link, or the click would
                        navigate to the routine instead of editing it. */}
                    <div className="truncate text-sm font-semibold text-foreground">
                      <InlineRename
                        value={workflow.name}
                        onRename={(next) => renameRoutine(workflow.id, next)}
                        inputClassName="text-sm font-semibold"
                      />
                    </div>
                    <Link
                      to="/workflows/$id"
                      params={{ id: workflow.id }}
                      className="block min-w-0"
                      aria-label={workflow.name}
                    >
                      <div className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[12px] text-muted-foreground">
                        {schedules.map((s) => (
                          <span key={s.id} className="inline-flex items-center gap-1">
                            <AlarmClockIcon className="size-3 shrink-0" aria-hidden />
                            {cronToHumanLocalized(s.scheduleCron ?? "", t)}
                          </span>
                        ))}
                        {nextRun ? (
                          <span className="text-muted-foreground/60">
                            {t("routines.nextRun", { when: nextRun })}
                          </span>
                        ) : null}
                      </div>
                    </Link>
                  </div>
                  <button
                    type="button"
                    className="shrink-0 rounded-md p-1.5 text-muted-foreground/50 transition hover:text-foreground disabled:opacity-60"
                    aria-label={t("routines.duplicateAria")}
                    disabled={duplicating === workflow.id}
                    onClick={() => void duplicateRoutine(workflow.id)}
                  >
                    {duplicating === workflow.id ? (
                      <Loader2Icon className="size-4 animate-spin" />
                    ) : (
                      <CopyIcon className="size-4" />
                    )}
                  </button>
                  <button
                    type="button"
                    className="shrink-0 rounded-md p-1.5 text-muted-foreground/50 transition hover:text-red-500"
                    aria-label={t("routines.deleteAria")}
                    onClick={() =>
                      void (async () => {
                        for (const s of schedules) {
                          await postJson("/api/morrow/workflow-triggers/delete", { id: s.id });
                        }
                        await refreshNow();
                      })()
                    }
                  >
                    <Trash2Icon className="size-4" />
                  </button>
                </div>

                {runs.length > 0 ? (
                  <>
                    <button
                      type="button"
                      onClick={() => {
                        setOpenRuns((cur) => (cur === workflow.id ? null : workflow.id));
                        setSelectedRunId(null);
                      }}
                      aria-expanded={openRuns === workflow.id}
                      className="flex w-fit items-center gap-1 text-[11px] font-medium text-muted-foreground transition hover:text-foreground"
                    >
                      <ChevronDownIcon
                        aria-hidden
                        className={`size-3 transition-transform ${openRuns === workflow.id ? "rotate-180" : ""}`}
                      />
                      {runs.length === 1
                        ? t("workflows.card.runs", { count: runs.length })
                        : t("workflows.card.runsPlural", { count: runs.length })}
                    </button>
                    {openRuns === workflow.id ? (
                      <ul className="flex flex-col gap-1 border-t border-border/50 pt-2">
                        {runs.slice(0, 10).map((r) => (
                          <li key={r.id} className="flex flex-col">
                            <button
                              type="button"
                              onClick={() =>
                                setSelectedRunId((cur) => (cur === r.id ? null : r.id))
                              }
                              className="flex min-w-0 items-center gap-1.5 text-left text-[12px]"
                            >
                              <StepStatusIcon
                                status={r.status === "running" ? "running" : r.status}
                              />
                              {/* WHEN it ran is the only thing that tells two runs of the same
                                  routine apart — the source and the cron are identical on every
                                  row, so leading with them made eleven rows look like one. */}
                              <span className="shrink-0 font-medium text-foreground">
                                {runWhen(r.createdAt)}
                              </span>
                              {runDetailLabel(r.detail, t) ? (
                                <span className="truncate text-muted-foreground">
                                  · {runDetailLabel(r.detail, t)}
                                </span>
                              ) : null}
                              <span className="ml-auto shrink-0 text-[10px] capitalize text-muted-foreground/60">
                                {r.status}
                              </span>
                            </button>
                            {selectedRunId === r.id ? (
                              <RunStepsPanel
                                runId={r.id}
                                graph={null}
                                runStatus={r.status}
                                threadId={r.threadId}
                              />
                            ) : null}
                          </li>
                        ))}
                      </ul>
                    ) : null}
                  </>
                ) : null}
              </li>
            );
          })}
        </ul>
      )}
    </MorrowWorkspacePage>
  );
}

/** A run's start time, as a reader scans it: "Today, 08:00" / "8 Oct, 08:00". */
function runWhen(iso: string): string {
  const at = new Date(iso);
  if (Number.isNaN(at.getTime())) return iso;
  const time = at.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" });
  const today = new Date();
  const sameDay =
    at.getDate() === today.getDate() &&
    at.getMonth() === today.getMonth() &&
    at.getFullYear() === today.getFullYear();
  if (sameDay) return `Today, ${time}`;
  return `${at.toLocaleDateString(undefined, { day: "numeric", month: "short" })}, ${time}`;
}
